"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { workspacesApi, type Workspace } from "@/lib/api";
import { useWorkspaceActivityAutoRefresh } from "@/hooks/useWorkspaceActivityAutoRefresh";
import {
  fetchReceivableDebtsDetails,
  resolveOwnerUserId,
  type ReceivableDebtsSummary,
  type ReceivableKind,
  type ReceivableListItem,
} from "@/lib/pendingOrders";
import { fetchWorkspaceTags } from "@/lib/workspaceAvatarTags";
import {
  refetchReceivablePendingWithRetry,
  subscribeReceivablePendingRefresh,
} from "@/lib/receivablePendingRefresh";

const EMPTY_SUMMARY: ReceivableDebtsSummary = {
  pendingCount: 0,
  reviewCount: 0,
  totalCount: 0,
  cardSubtitle: "ไม่มีรายการค้าง",
  detailNowLabel: "ไม่มีรายการค้างชำระ",
};

function unwrapWorkspace(data: unknown): Workspace | null {
  if (!data || typeof data !== "object") return null;
  const maybe = data as { data?: unknown; workspace?: unknown };
  const inner = maybe.data ?? maybe.workspace ?? data;
  if (!inner || typeof inner !== "object") return null;
  return inner as Workspace;
}

type RefetchOptions = {
  silent?: boolean;
  /** Bypass in-flight dedupe so post-match/undo never reuses a stale fetch. */
  force?: boolean;
};

export function useWorkspaceReceivableDetails(
  workspaceId?: string | null,
  workspaceProp?: Workspace | null,
) {
  const [workspace, setWorkspace] = useState<Workspace | null>(
    workspaceProp ?? null,
  );
  const [summary, setSummary] = useState<ReceivableDebtsSummary>(EMPTY_SUMMARY);
  const [items, setItems] = useState<ReceivableListItem[]>([]);
  const [itemsByKind, setItemsByKind] = useState<
    Record<ReceivableKind, ReceivableListItem[]>
  >({
    announcement: [],
    address: [],
    slip: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refetchGenerationRef = useRef(0);

  useEffect(() => {
    const wsId = workspaceProp?.id?.trim() || workspaceId?.trim() || "";
    if (!wsId) {
      setWorkspace(workspaceProp ?? null);
      return;
    }

    let cancelled = false;

    const applyWorkspace = (next: Workspace | null) => {
      if (!cancelled) setWorkspace(next);
    };

    const hasOwnerTag = (ws: Workspace | null | undefined) =>
      Boolean(
        ws?.tags?.some((tag) => tag.key?.trim() === "owner_user_id" && tag.value?.trim()),
      );

    if (workspaceProp && hasOwnerTag(workspaceProp)) {
      applyWorkspace(workspaceProp);
      return () => {
        cancelled = true;
      };
    }

    void (async () => {
      try {
        const [raw, tags] = await Promise.all([
          workspaceProp ? Promise.resolve(workspaceProp) : workspacesApi.getById(wsId),
          fetchWorkspaceTags(wsId),
        ]);
        const base = workspaceProp ?? unwrapWorkspace(raw);
        if (!base) {
          applyWorkspace(tags.length > 0 ? ({ id: wsId, name: "", tags } as Workspace) : null);
          return;
        }
        applyWorkspace({
          ...base,
          id: base.id || wsId,
          tags: tags.length > 0 ? tags : (base.tags ?? []),
        });
      } catch {
        if (workspaceProp) {
          applyWorkspace(workspaceProp);
          return;
        }
        try {
          const tags = await fetchWorkspaceTags(wsId);
          applyWorkspace(
            tags.length > 0 ? ({ id: wsId, name: "", tags } as Workspace) : ({ id: wsId } as Workspace),
          );
        } catch {
          applyWorkspace({ id: wsId } as Workspace);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [workspaceId, workspaceProp]);

  const ownerUserId = useMemo(
    () => resolveOwnerUserId(workspace),
    [workspace],
  );

  const refetch = useCallback(
    async (options?: RefetchOptions) => {
      if (!ownerUserId) {
        setSummary(EMPTY_SUMMARY);
        setItems([]);
        setItemsByKind({ announcement: [], address: [], slip: [] });
        setError(null);
        setIsLoading(false);
        return;
      }

      const silent = options?.silent ?? false;
      const force = options?.force ?? false;
      const requestId = ++refetchGenerationRef.current;

      try {
        if (!silent) setIsLoading(true);
        setError(null);
        const details = await fetchReceivableDebtsDetails(ownerUserId, { force });
        // Ignore outdated responses (e.g. pre-match auto-refresh finishing after a forced post-match fetch).
        if (requestId !== refetchGenerationRef.current) return;
        setSummary(details.summary);
        setItems(
          details.lists.items.map((item) => ({
            ...item,
            statuses: [...item.statuses],
            fields: [...item.fields],
          })),
        );
        setItemsByKind({
          announcement: details.lists.byKind.announcement.map((item) => ({
            ...item,
            statuses: [...item.statuses],
            fields: [...item.fields],
          })),
          address: details.lists.byKind.address.map((item) => ({
            ...item,
            statuses: [...item.statuses],
            fields: [...item.fields],
          })),
          slip: details.lists.byKind.slip.map((item) => ({
            ...item,
            statuses: [...item.statuses],
            fields: [...item.fields],
          })),
        });
      } catch (err) {
        if (requestId !== refetchGenerationRef.current) return;
        const message = err instanceof Error ? err.message : "unknown error";
        setError(message);
        setSummary(EMPTY_SUMMARY);
        setItems([]);
        setItemsByKind({ announcement: [], address: [], slip: [] });
      } finally {
        if (!silent && requestId === refetchGenerationRef.current) {
          setIsLoading(false);
        }
      }
    },
    [ownerUserId],
  );

  useEffect(() => {
    void refetch();
  }, [refetch]);

  useWorkspaceActivityAutoRefresh(workspaceId, () => refetch({ silent: true }));

  useEffect(() => {
    return subscribeReceivablePendingRefresh((detail) => {
      void refetchReceivablePendingWithRetry(refetch, detail);
    });
  }, [refetch]);

  return {
    ownerUserId,
    summary,
    items,
    itemsByKind,
    isLoading,
    error,
    refetch,
  };
}

export type { ReceivableListItem };
